import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  ViewChild
} from '@angular/core';
import {Platform} from '@angular/cdk/platform';
import SignaturePad, {PointGroup} from 'signature_pad';
import {combineLatest, fromEvent, interval, Subscription} from 'rxjs';
import {startWith, throttle} from 'rxjs/operators';
import {Point} from '@angular/cdk/drag-drop';

@Component({
  template: `
    <canvas #canvas *ngIf="platform.IOS; else regularcanvas" (touchstart)="absorbEvent($event)"
            (touchmove)="absorbEvent($event)"
            (touchend)="absorbEvent($event)"
            (touchcancel)="absorbEvent($event)">
    </canvas>
    <ng-template #regularcanvas><canvas #canvas></canvas></ng-template>`,
  selector: 'pl-signature-pad',
  styleUrls: ['signature-pad.component.scss']
})

export class SignaturePadComponent implements AfterViewInit, OnDestroy {
  public platform = inject(Platform);
  public cdr = inject(ChangeDetectorRef);

  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>;

  private subs = new Subscription();
  private signaturePad: SignaturePad;
  private data: PointGroup[];
  private lastSize: Point;

  ngAfterViewInit() {
    const canvas: HTMLCanvasElement = this.canvasRef.nativeElement;
    this.signaturePad = new SignaturePad(canvas, {});
    this.lastSize = {x: canvas.offsetWidth, y: canvas.offsetHeight};

    this.subs.add(combineLatest([
      fromEvent(window, 'resize').pipe(startWith(null)),
      fromEvent(window, 'orientationchange').pipe(startWith(null))
    ])
      .pipe(throttle(ev => interval(100), {leading: true, trailing: true}))
      .subscribe(e => {
        this.resizeCanvas();
      }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  public absorbEvent(event: Event) {
    if (!event) {
      return;
    }

    event.preventDefault?.();
    event.stopPropagation?.();
    event.cancelBubble = true;
    event.returnValue = false;
    return false;
  }

  public resizeCanvas(): void {
    this.data = this.toData();

    // When zoomed out to less than 100%, for some very strange reason,
    // some browsers report devicePixelRatio as less than 1
    // and only part of the canvas is cleared then.
    const ratio: number = Math.max(window.devicePixelRatio || 1, 1);
    const canvas: HTMLCanvasElement = this.canvasRef.nativeElement;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    this.signaturePad.clear(); // otherwise isEmpty() might return incorrect value

    this.resizePoints(this.data, this.lastSize.x);

    this.lastSize = {x: canvas.offsetWidth, y: canvas.offsetHeight};
    this.fromData(this.data);
  }

  // Returns signature image as an array of point groups
  public toData(): Array<PointGroup> {
    if (this.signaturePad) {
      return this.signaturePad.toData();
    } else {
      return [];
    }
  }

  // Draws signature image from an array of point groups
  public fromData(points: PointGroup[], signaturePadSize?: Point): void {
    if (signaturePadSize
      && signaturePadSize.x > 0
      && signaturePadSize.y > 0
      && this.canvasRef?.nativeElement
      && signaturePadSize.x !== this.canvasRef.nativeElement.offsetWidth) {
      this.resizePoints(points, signaturePadSize.x);
    }

    this.signaturePad.fromData(points);
    this.cdr.markForCheck();
  }

  // Returns signature image as data URL (see https://mdn.io/todataurl for the list of possible paramters)
  public toDataURL(imageType?: string, quality?: number): string {
    return this.signaturePad.toDataURL(imageType, quality); // save image as data URL
  }

  // Clears the canvas
  public clear(): void {
    this.data = [];
    this.signaturePad.clear();
  }

  // Returns true if canvas is empty, otherwise returns false
  public isEmpty(): boolean {
    return this.signaturePad.isEmpty();
  }

  // Unbinds all event handlers
  public off(): void {
    this.signaturePad.off();
  }

  // Rebinds all event handlers
  public on(): void {
    this.signaturePad.on();
  }

  private resizePoints(points: PointGroup[], oldWidth: number) {
    const factor = this.canvasRef.nativeElement.offsetWidth / oldWidth;
    points.forEach(element => {
      element.points.forEach(point => {
        point.x *= factor;
        point.y *= factor;
      });
      element.minWidth *= factor;
      element.maxWidth *= factor;
    });
  }
}
